import { AirportPopupComponent } from './../components/AirportPopup/AirportPopup'
import { DetailsPanel } from '../components/DetailsPanel/DetailsPanel'
import { SearchBar } from '../components/SearchBar/SearchBar'
import SecurityCMS from './api/SecurityCMS/ApiService'
import MapBox from '../modules/map'
import { config } from '../Config'
import { cloneDeep } from 'lodash'
import { extractImageRelativePathFromDOMString } from './utils'
import { EventDetailsPanel } from '../components/EventDetailsPanel/EventDetailsPanel'
import BottomNavigationService from './BottomNavigation'
import { getAirportDetailProperties } from '../services/AirportService'
import User from '../modules/user'

export const getAirportsFeatureCollection = (airportsData, international = true, domestic = true, military = true, civilian = true, joint = true, privateAirports = true) => {
  const featureCollection = {
    type: 'FeatureCollection',
    features: []
  }

  const filteredAirports = []

  // From the whole list of airports we find and add only those we are interested in, we continue to the next iteration as 
  // soon as an airport meets at least one filtering requirment
  airportsData.forEach(airport => {
    // (international OR domestic) AND (military OR civilian OR joint)
    if (
      (
        (international && (airport.AirportOfEntry === 'true')) ||
        (domestic && airport.AirportOfEntry === 'false')
      ) &&
      (
        (military && airport.Type === 'Military') ||
        (civilian && airport.Type === 'Civil') ||
        (joint && airport.Type === 'Joint Civil / Military') ||
        (privateAirports && airport.Type === 'Private')
      )
    ) {
      filteredAirports.push(airport)
    }
  })

  for (const airport of filteredAirports) {
    if (!airport.Lat || !airport.Lng || !airport.ICAO) continue

    const properties = getAirportDetailProperties(airport)

    const point = {
      type: 'Point',
      coordinates: [airport.Lng, airport.Lat]
    }

    const feature = {
      type: 'Feature',
      id: `${airport.Id}.${airport.ICAO}`,
      geometry: point,
      properties: properties
    }

    featureCollection.features.push(feature)
  }

  return featureCollection
}

// TODO: this one needs a little of refactoring as it doesn't feet well inside a Service. It triggers map events which should be more likely part of
// the component or the Map module, and because of that it includes UI components like DetailsPanel specific to the Security module
// which makes this file not good to import into other sections like Luxury Yachting.
export const addAirportsToMap = (airportsFeatureCollection) => {
  // If this function gets called for any reason after the sources were defined, then ignore it
  if (MapBox.map.getSource('airports') !== undefined) return

  MapBox.map.addSource('airports', {
    type: 'geojson',
    data: airportsFeatureCollection,
    cluster: true,
    clusterMaxZoom: 14, // Max zoom to cluster points on
    clusterRadius: 25 // Radius of each cluster when clustering points (defaults to 50)
  })

  MapBox.map.addSource('airports-unclustered', {
    type: 'geojson',
    data: airportsFeatureCollection
  })

  MapBox.map.addSource('countryTiles', {
    type: 'vector',
    url: 'mapbox://mapbox.country-boundaries-v1'
  })

  createMapLayers()
  addMapEventListeners()
}

export const updateMapAirports = (airportsFeatureCollection) => {
  MapBox.map.getSource('airports').setData(airportsFeatureCollection)
  MapBox.map.getSource('airports-unclustered').setData(airportsFeatureCollection)
}

export const updateMapRiskLayer = (riskType = 'airport') => {
  let riskTypeProperty = 'travelRating'
  if (riskType === 'city') riskTypeProperty = 'cityRating'
  if (riskType === 'country') riskTypeProperty = 'countryRating'
  if (riskType === 'airport') riskTypeProperty = 'airportRating'

  // Change airports layout icon image based on the setting
  MapBox.map.setLayoutProperty('airports', 'icon-image', [
    'match',
    ['get', riskTypeProperty],
    1, 'airport-marker-1',
    2, 'airport-marker-2',
    3, 'airport-marker-3',
    4, 'airport-marker-4',
    5, 'airport-marker-5',
    'airport-marker'
  ])

  // Change unclustered points pain property colors (keep separated for better control)
  MapBox.map.setPaintProperty('airports-points', 'circle-color', [
    'match',
    ['get', riskTypeProperty],
    1, '#FFFFFF',
    2, '#00FF66',
    3, '#ED6E33',
    4, '#D44333',
    5, '#82170B',
    '#5a6168'
  ])

  MapBox.map.setPaintProperty('airports-points', 'circle-blur', [
    'match',
    ['get', riskTypeProperty],
    1, 1,
    2, 1,
    3, 1,
    4, 1,
    5, 1,
    0
  ])

  MapBox.map.setPaintProperty('airports-points', 'circle-radius', [
    'match',
    ['get', riskTypeProperty],
    1, 5,
    2, 5,
    3, 5,
    4, 5,
    5, 5,
    2
  ])
}

// Airport icon display ICAO code by default, but this can be changed based on the user settings
export const updateAirportIconCode = (airportCode) => {
  if (MapBox.map.getLayer('airports') !== undefined) {
    let airportCodeLowerCased = airportCode.toLowerCase()
    if (airportCodeLowerCased === 'icao') {
      MapBox.map.setLayoutProperty('airports', 'text-field', ['get', 'icao'])
    } else if (airportCodeLowerCased === 'iata')
      MapBox.map.setLayoutProperty('airports', 'text-field', ['get', 'displayIATA'])
  }
}

export const createMapLayers = () => {
  MapBox.map.addLayer({
    id: 'clusters',
    type: 'circle',
    source: 'airports',
    minzoom: 3.5,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': '#000000',
      'circle-radius': 12
    }
  })

  MapBox.map.addLayer({
    id: 'cluster-count',
    type: 'symbol',
    source: 'airports',
    minzoom: 3.5,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
      'text-size': 12,
      'text-allow-overlap': true
    },
    paint: {
      'text-color': '#ffffff'
    }
  })

  MapBox.map.addLayer({
    id: 'airports',
    type: 'symbol',
    source: 'airports',
    minzoom: 3.5,
    paint: {
      'text-color': '#ffffff',
      'text-halo-width': 1,
      'text-halo-color': '#000000'
    },
    filter: ['!', ['has', 'point_count']],
    layout: {
      'icon-image': 'airport-marker',
      'icon-allow-overlap': true,
      'text-allow-overlap': true,
      'icon-size': .5,
      'text-field': ['get', 'icao'],
      'text-font': [
        'Open Sans Semibold',
        'Arial Unicode MS Bold'
      ],
      'text-offset': [0, 1],
      'text-anchor': 'top'
    }
  })

  MapBox.map.addLayer({
    id: 'airports-points',
    type: 'circle',
    source: 'airports-unclustered',
    maxzoom: 3.5
  })

  updateMapRiskLayer()
}

export const highlightCountry = (ISO_3166_1_ALPHA_2, longitude, latitude, color) => {
  const existingLayer = MapBox.map.getLayer('country-layer')
  if (existingLayer === undefined) {
    // Country fill
    MapBox.map.addLayer({
      id: 'country-layer',
      type: 'fill',
      source: 'countryTiles',
      'source-layer': 'country_boundaries',
      filter: ['any', ['all', ['==', ['get', 'iso_3166_1'], ISO_3166_1_ALPHA_2]]],
      paint: {
        'fill-color': color?.fillColor ? color.fillColor : 'rgba(95, 233, 208, 0.1)',
        'fill-outline-color': color?.outLineColor ? color.outLineColor : '#3CDEC1'
      }
    })

    // Country boundary line
    MapBox.map.addLayer({
      id: 'country-layer-line',
      type: 'line',
      source: 'countryTiles',
      'source-layer': 'country_boundaries',
      filter: ['any', ['all', ['==', ['get', 'iso_3166_1'], ISO_3166_1_ALPHA_2]]],
      paint: {
        'line-color': color?.outLineColor ? color.outLineColor : '#3CDEC1', 'line-width': 6,
        'line-blur': 4
      }
    })
  } else {
    MapBox.map.setPaintProperty('country-layer', 'fill-color', color?.fillColor ? color.fillColor : 'rgba(95, 233, 208, 0.1)')
    MapBox.map.setPaintProperty('country-layer-line', 'line-color', color?.outLineColor ? color.outLineColor : '#3CDEC1')
    setTimeout(() => {
      MapBox.map.setFilter('country-layer', ['any', ['all', ['==', ['get', 'iso_3166_1'], ISO_3166_1_ALPHA_2]]])
      MapBox.map.setFilter('country-layer-line', ['any', ['all', ['==', ['get', 'iso_3166_1'], ISO_3166_1_ALPHA_2]]])
      MapBox.map.setLayoutProperty('country-layer', 'visibility', 'visible')
      MapBox.map.setLayoutProperty('country-layer-line', 'visibility', 'visible')
    },1)
  }

  // TODO: a good improvement would be to fit the country layer inside the current view with fitBounds
  // https://gist.github.com/danswick/83a8ddff7fb9193176a975a02a896792
  // https://gis.stackexchange.com/a/308746
  // https://stackoverflow.com/a/34404670/2052173
  // Currently we just center to the airport of interest and set a fixed zoom.
  MapBox.map.flyTo({ center: ['' + longitude, '' + latitude], zoom: 3.45 })
}

export const mapflyToLocation = (longitude, latitude) => {
  MapBox.map.flyTo({ center: ['' + longitude, '' + latitude], zoom: 3.45 })
}

export const unhighlightCountry = (locationCoordinates, zoomLevel = 8) => {
  const existingLayer = MapBox.map.getLayer('country-layer')
  if (existingLayer !== undefined) {
    MapBox.map.setLayoutProperty('country-layer', 'visibility', 'none')
    MapBox.map.setLayoutProperty('country-layer-line', 'visibility', 'none')

    if (locationCoordinates !== undefined) {
      MapBox.map.flyTo({ center: locationCoordinates, zoom: zoomLevel })
    }
  }
}

const addMapEventListeners = () => {
  let userAirportCodePreference = 'ICAO'
  window.setInterval(() => userAirportCodePreference = User.userSettings.airportCode, 2500)

  MapBox.map.on('mouseenter', 'airports', (e) => {
    AirportPopupComponent.$data.display = 'block'
    AirportPopupComponent.$data.opacity = 1

    const airportData = e.features[0].properties
    AirportPopupComponent.$data.airportCode = userAirportCodePreference === 'ICAO' ? airportData.icao : (!!airportData.iata ? airportData.iata : airportData.icao)
    AirportPopupComponent.$data.airportName = airportData.airportName
    AirportPopupComponent.$data.city = airportData.cityName
    AirportPopupComponent.$data.country = airportData.country
    AirportPopupComponent.$data.cityRating = airportData.cityRating
    AirportPopupComponent.$data.countryRating = airportData.countryRating
    AirportPopupComponent.$data.airportRating = airportData.airportRating
    AirportPopupComponent.$data.airspaceRating = airportData.airspaceRating

    MapBox.map.getCanvas().style.cursor = 'pointer'
  })

  MapBox.map.on('mouseleave', 'airports', () => {
    AirportPopupComponent.$data.display = 'none'
    AirportPopupComponent.$data.opacity = 0
    MapBox.map.getCanvas().style.cursor = 'inherit'
  })

  MapBox.map.on('mousemove', 'airports', (e) => {
    AirportPopupComponent.$data.leftPosition = e.point.x + 15 + 'px'
    AirportPopupComponent.$data.topPosition = e.point.y + 15 + 'px'
  })

  // Inspect a cluster on click
  MapBox.map.on('click', 'clusters', (e) => {
    const features = MapBox.map.queryRenderedFeatures(e.point, {
      layers: ['clusters']
    })

    const clusterId = features[0].properties.cluster_id
    MapBox.map.getSource('airports').getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err) return

      MapBox.map.easeTo({
        center: features[0].geometry.coordinates,
        zoom: zoom
      })
    })
  })

  MapBox.map.on('mouseenter', 'clusters', () => MapBox.map.getCanvas().style.cursor = 'pointer')
  MapBox.map.on('mouseleave', 'clusters', () => MapBox.map.getCanvas().style.cursor = 'inherit')

  MapBox.map.on('click', (e) => {
    SearchBar.hideFilters()
    BottomNavigationService.hide()
  })
}

export const airportDetails = {
  overview: {
    runwaySpecs: '',
    groundHandling: {},
    airportEntry: {},
    hoursOfOperations: {},
    airportContacts: '',
    airportMapURL: ''
  },
  operations: {
    airportSummary: '',
    groundTransport: '',
  },
  security: {
      assessment: ''
  },
  medical: {
    vaccinationRequirements: '',
  }
}

export const getAirportDetails = (airportICAO) => {
  const airportResults = SecurityCMS.getAirportDetails(airportICAO)
  let airportDetails = cloneDeep(airportDetails)

  airportResults.forEach((result) => {
    if (result.Name === 'Ground Handling' && result.Value.Body !== 'null')
      airportDetails.overview.groundHandling = JSON.parse(result.Value.Body)

    if (result.Name === 'Airport Of Entry' && result.Value.Body !== 'null')
      airportDetails.overview.airportEntry = JSON.parse(result.Value.Body)

    if (result.Name === 'Airport Contacts' && result.Value.Content !== '')
      airportDetails.overview.airportContacts = result.Value.Content

    if (result.Name === 'Airport map' && result.Value.Content !== '')
      airportDetails.overview.airportMapURL = config.MedaireSecurityCMSPortal + extractImageRelativePathFromDOMString(result.Value.Content)

    if (result.Name === 'Airport Summary' && result.Value.Content !== '')
      airportDetails.operations.airportSummary = result.Value.Content

    if (result.Name === 'Ground Transportation' && result.Value.Content !== '')
      airportDetails.operations.groundTransport = result.Value.Content

    if (result.Name === 'Airport Security' && result.Value.Content !== '')
      airportDetails.security.assessment = result.Value.Content

    if (result.Name === 'Vaccination Requirements' && result.Value.Content !== '')
      airportDetails.medical.vaccinationRequirements = result.Value.Content
  })

  return airportResults
}